import React, { useState } from "react";
import { MapPin, Phone, Instagram, Clock, Send } from "lucide-react";
import { clinic, whatsappLink, mapsLink } from "../mock";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { useToast } from "../hooks/use-toast";

const Contact = () => {
  const { toast } = useToast();
  const [form, setForm] = useState({ name: "", phone: "", message: "" });

  const handleSubmit = (e) => {
    e.preventDefault();
    const text = `Olá, meu nome é ${form.name || "(sem nome)"}. ${
      form.message || "Gostaria de agendar uma consulta."
    }${form.phone ? ` Meu telefone: ${form.phone}.` : ""}`;
    const url = `https://api.whatsapp.com/send?phone=${clinic.phoneDigits}&text=${encodeURIComponent(text)}`;
    window.open(url, "_blank");
    toast({ title: "Redirecionando para o WhatsApp", description: "Vamos continuar seu atendimento por lá." });
  };

  const addr = clinic.address;

  return (
    <section id="contato" className="py-20 md:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-stretch">
          <div className="reveal">
            <span className="text-xs font-semibold tracking-widest uppercase text-[#b89a5a]">
              Contato
            </span>
            <div className="gold-line mt-3" />
            <h2 className="font-display font-bold text-3xl md:text-4xl text-[#2f3a45] mt-5">
              Agende sua consulta hoje
            </h2>
            <p className="text-[#4a5a68] mt-4 leading-relaxed">
              Entre em contato para mais informações sobre nossos serviços. Será um
              prazer cuidar da sua visão.
            </p>

            <div className="mt-8 space-y-5">
              <a href={mapsLink} target="_blank" rel="noopener noreferrer" className="flex items-start gap-4 group">
                <span className="w-12 h-12 rounded-xl bg-[#e8eaed] flex items-center justify-center shrink-0 group-hover:bg-[#b89a5a]/15 transition-colors">
                  <MapPin className="w-6 h-6 text-[#b89a5a]" />
                </span>
                <div>
                  <div className="font-display font-semibold text-[#2f3a45]">{addr.building}</div>
                  <div className="text-[#6b7a88] text-sm mt-0.5">
                    {addr.street}, {addr.room} — {addr.district}<br />
                    {addr.city}-{addr.state} · CEP: {addr.cep}
                  </div>
                </div>
              </a>

              <a href={whatsappLink} target="_blank" rel="noopener noreferrer" className="flex items-center gap-4 group">
                <span className="w-12 h-12 rounded-xl bg-[#e8eaed] flex items-center justify-center shrink-0 group-hover:bg-[#b89a5a]/15 transition-colors">
                  <Phone className="w-6 h-6 text-[#b89a5a]" />
                </span>
                <div>
                  <div className="font-display font-semibold text-[#2f3a45]">WhatsApp</div>
                  <div className="text-[#6b7a88] text-sm">{clinic.phone}</div>
                </div>
              </a>

              <a href={clinic.instagramUrl} target="_blank" rel="noopener noreferrer" className="flex items-center gap-4 group">
                <span className="w-12 h-12 rounded-xl bg-[#e8eaed] flex items-center justify-center shrink-0 group-hover:bg-[#b89a5a]/15 transition-colors">
                  <Instagram className="w-6 h-6 text-[#b89a5a]" />
                </span>
                <div>
                  <div className="font-display font-semibold text-[#2f3a45]">Instagram</div>
                  <div className="text-[#6b7a88] text-sm">@{clinic.instagram}</div>
                </div>
              </a>

              <div className="flex items-center gap-4">
                <span className="w-12 h-12 rounded-xl bg-[#e8eaed] flex items-center justify-center shrink-0">
                  <Clock className="w-6 h-6 text-[#b89a5a]" />
                </span>
                <div>
                  <div className="font-display font-semibold text-[#2f3a45]">Horário</div>
                  <div className="text-[#6b7a88] text-sm">Seg a Sex · 08h às 18h</div>
                </div>
              </div>
            </div>
          </div>

          <div className="reveal bg-[#e8eaed]/50 rounded-[2rem] p-7 md:p-9">
            <h3 className="font-display font-semibold text-xl text-[#2f3a45]">
              Solicite seu agendamento
            </h3>
            <form onSubmit={handleSubmit} className="mt-6 space-y-4">
              <div>
                <label className="text-sm font-medium text-[#2f3a45]">Nome</label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Seu nome completo"
                  className="mt-1.5 bg-white border-[#d5dae0] h-12 rounded-xl"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-[#2f3a45]">Telefone</label>
                <Input
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  placeholder="(83) 90000-0000"
                  className="mt-1.5 bg-white border-[#d5dae0] h-12 rounded-xl"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-[#2f3a45]">Mensagem</label>
                <Textarea
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  placeholder="Conte-nos como podemos ajudar"
                  rows={4}
                  className="mt-1.5 bg-white border-[#d5dae0] rounded-xl resize-none"
                />
              </div>
              <Button
                type="submit"
                className="w-full bg-[#b89a5a] hover:bg-[#a5884b] text-white rounded-full h-12 text-base shadow-md hover:shadow-lg transition-all"
              >
                <Send className="w-4 h-4 mr-2" /> Enviar pelo WhatsApp
              </Button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
